/* eslint-disable @typescript-eslint/no-explicit-any */
import { DeliveryOptionEnum } from '@/entities';
import { ChainCurrencyEnum, ChainThemeEnum, ClientChainSettingsDto, ItemPhotoTypeEnum } from './api/restApi';
import { BasketState, initialOrderDeliveryState, InitState, IOrderOptions, OrderState } from './slices';

export const migrations = {
  0: (state: any) => state, // Initial version
  1: (state: any) => {
    // Migration from version 0 to 1 - restructuring order slice
    return {
      ...state,

      order: {
        // old values without deliveryAddress
        name: state.order.name,
        phone: state.order.phone,
        commentary: state.order.commentary,
        takeoutAddress: state.order.takeoutAddress,
        deliveryType: state.order.deliveryType,
        paymentType: state.order.paymentType,
        // new value
        delivery: initialOrderDeliveryState,
      },
    };
  },

  2: (state: any) => {
    // Migration from version 1 to 2 - adding paymentTypes to order.delivery
    return {
      ...state,
      order: { ...state.order, delivery: { ...state.order.delivery, cityCoords: undefined, paymentTypes: undefined } },
    };
  },

  3: (state: any) => {
    // Migration from version 2 to 3 - adding timeSlots, infoLoading and fulfillmentToTime to order

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { loading, ...deliveryWithoutLoading } = state.order.delivery;
    return {
      ...state,
      order: {
        ...state.order,
        timeSlots: [],
        infoLoading: false,
        fulfillmentToTime: undefined,
        delivery: deliveryWithoutLoading,
      },
    };
  },

  4: (state: any) => {
    // Migration from version 3 to 4 - adding variants to basket.products
    return {
      ...state,
      basket: { ...state.basket, products: state.basket.products.map((p: any) => ({ ...p, variants: undefined })) },
    };
  },

  5: (state: any) => {
    // Migration from version 4 to 5 - adding cutleryEnabled and cutleryQuantity to basket
    return {
      ...state,
      basket: { ...state.basket, cutleryEnabled: false, cutleryQuantity: 1 },
    };
  },

  6: (state: any) => {
    // Migration from version 5 to 6:
    // adding options to order
    // deleting cutleryEnabled, cutleryQuantity from basket
    // addings settings to initial and deleting currency from it
    return {
      ...state,
      order: {
        ...state.order,
        options: { eatAtThePlace: false, leaveAtTheDoor: false, cutleryQuantity: 1 } as IOrderOptions,
      } as OrderState,
      basket: {
        products: state.basket.products,
        totalCheck: state.basket.totalCheck,
        numberOfProducts: state.basket.numberOfProducts,
      } as BasketState,
      initial: {
        viewedBanners: [],
        tgData: state.initial.tgData,
        chainId: state.initial.chainId,
        settings: {
          cutleryEnabled: false,
          eatAtPlaceOptionEnabled: false,
          leaveOrderAtDoorOptionEnabled: false,
          currency: ChainCurrencyEnum.Rub,
        } as ClientChainSettingsDto,
      } as InitState,
    };
  },

  7: (state: any) => {
    // Phone number reset
    return { ...state, order: { ...state.order, phone: '' } as OrderState };
  },

  8: (state: any) => {
    // Migration from version 7 to 8 - Add noCallIsNeededOptionEnabled to initial slice and noCallNeeded to order slice
    return {
      ...state,
      order: {
        ...state.order,
        noCallNeeded: false,
      } as OrderState,
      initial: {
        ...state.initial,
        settings: { ...state.initial.settings, noCallIsNeededOptionEnabled: false },
      } as InitState,
    };
  },

  9: (state: any) => {
    // Migration from version 8 to 9 - replacing timeSlots with scheduleSlots in orderSlice
    return {
      ...state,
      order: { ...state.order, scheduleSlots: [] } as OrderState,
    };
  },

  10: (state: any) => {
    // Migration from version 9 to 10 - adding theme to initial slice
    return {
      ...state,
      initial: {
        ...state.initial,
        settings: {
          ...state.initial.settings,
          theme: {
            accentColor: '#F97316',
            firstColor: '#FFF7ED',
            secondColor: '#FED7AA',
            analogColor: '#10B981',
            name: ChainThemeEnum.Light,
          },
        },
      } as InitState,
    };
  },
  11: (state: any) => {
    // Migration from version 10 to 11 - adding city, street, house fields to order.delivery
    return {
      ...state,
      order: {
        ...state.order,
        delivery: {
          ...state.order.delivery,
          city: '',
          street: '',
          house: '',
        },
      } as OrderState,
    };
  },
  12: (state: any) => {
    // Migration from version 11 to 12 - adding areManualCoordinates and pointLabel fields to order.delivery
    return {
      ...state,
      order: {
        ...state.order,
        delivery: {
          ...state.order.delivery,
          areManualCoordinates: false,
          pointLabel: state.order.delivery.address,
        },
      } as OrderState,
    };
  },
  13: (state: any) => {
    // Migration from version 12 to 13 - adding nationwideInfo to order.delivery
    return {
      ...state,
      order: { ...state.order, delivery: { ...state.order.delivery, nationwideInfo: undefined } } as OrderState,
    };
  },

  14: (state: any) => {
    // Migration from version 13 to 14 - adding email and delivery.onlinePayment to order slice
    return {
      ...state,
      order: {
        ...state.order,
        email: '',
        delivery: { ...state.order.delivery, onlinePayment: undefined },
      } as OrderState,
    };
  },

  15: (state: any) => {
    // Migration from version 14 to 15 - adding orderPointDelivery to order slice
    return {
      ...state,
      order: {
        ...state.order,
        deliveryOption: DeliveryOptionEnum.Courier,
        orderPointDelivery: {
          userOrderPoint: undefined,
          orderPointAddress: '',
          orderPointCity: undefined,
          orderPointCityCoords: undefined,
        },
      } as OrderState,
      initial: {
        ...state.initial,
        settings: { ...state.initial.settings, theme: { ...state.initial.settings.theme, thirdColor: '#FFFDFA' } },
      } as InitState,
    };
  },

  16: (state: any) => {
    // Migration from version 15 to 16 - adding showBrandLogo to initial slice
    return {
      ...state,
      initial: { ...state.initial, settings: { ...state.initial.settings, showBrandLogo: false } } as InitState,
      order: {
        ...state.order,
        orderPointDelivery: {
          ...state.order.orderPointDelivery,
          orderPointArea: undefined,
          userOrderPoint: undefined,
          orderPointAddress: '',
          orderPointCity: undefined,
          orderPointCityCoords: undefined,
        },
      } as OrderState,
    };
  },

  17: (state: any) => {
    // Migration from version 16 to 17 - adding brendLogo to initial slice
    return {
      ...state,
      initial: { ...state.initial, brendLogo: undefined } as InitState,
    };
  },

  18: (state: any) => {
    // Migration from version 17 to 18 - adding promocode to order slice
    return {
      ...state,
      order: { ...state.order, promocode: undefined } as OrderState,
    };
  },

  19: (state: any) => {
    // Migration from version 18 to 19 - adding viewedBanners to initial slice
    return {
      ...state,
      initial: { ...state.initial, viewedBanners: [] } as InitState,
    };
  },

  20: (state: any) => {
    // Migration from version 19 to 20 - adding promoLoading to order slice
    return {
      ...state,
      order: { ...state.order, promoLoading: false } as OrderState,
    };
  },

  21: (state: any) => {
    // Migration from version 20 to 21 - removing commentsEnabled from order slice
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { commentsEnabled, ...order } = state.order;

    return {
      ...state,
      order: order as OrderState,
      initial: {
        ...state.initial,
        settings: { ...state.initial.settings, commentsEnabled: false, isOrderForAnotherPersonEnabled: false },
      } as InitState,
    };
  },

  22: (state: any) => {
    // Migration from version 21 to 22 - adding bonusSystem to initial slice, locationId to order.delivery and unavailableProductIds to basket
    return {
      ...state,
      order: {
        ...state.order,
        delivery: { ...state.order.delivery, locationId: undefined },
      } as OrderState,
      basket: { ...state.basket, unavailableProductIds: [] } as BasketState,
      initial: {
        ...state.initial,
        settings: { ...state.initial.settings, isBonusSystemEnabled: false, bonusSystem: null },
      } as InitState,
    };
  },

  23: (state: any) => {
    // Migration 22 -> 23: events slice now stores SentAttribution objects
    // ({ channel, utmCampaignId?, at }) per step instead of bare *At timestamps.
    // Drop the old shape; funnel throttle state is ephemeral so a reset is safe.
    return {
      ...state,
      events: { pendingCatalogReopen: false },
    };
  },

  24: (state: any) => {
    // Migration from version 23 to 24 - adding handleStocks to initial slice settings
    return {
      ...state,
      initial: { ...state.initial, settings: { ...state.initial.settings, handleStocks: false } } as InitState,
    };
  },

  25: (state: any) => {
    // Migration from version 24 to 25 - adding itemPhotoType to initial slice settings
    return {
      ...state,
      initial: {
        ...state.initial,
        settings: { ...state.initial.settings, itemPhotoType: ItemPhotoTypeEnum.Square },
      } as InitState,
    };
  },

  26: (state: any) => {
    // Migration from version 25 to 26 - adding catalogBanners to initial slice
    return {
      ...state,
      initial: { ...state.initial, catalogBanners: undefined } as InitState,
    };
  },
};
